import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, CreditCard, Wallet as WalletIcon, Check, ShieldCheck, Repeat, IndianRupee } from 'lucide-react';
import { useApp } from '../context/AppContext';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const SLOTS = ['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '04:00 PM', '06:00 PM'];

const Checkout = () => {
  const nav = useNavigate();
  const {
    cart, placeBooking, address, clearCart,
    coupon, clearCoupon, computeCouponDiscount,
    coins, setCoins, coinsRedeem, setCoinsRedeem,
  } = useApp();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [slot, setSlot] = useState('10:00 AM');
  const [payment, setPayment] = useState('card');
  const [addr, setAddr] = useState(address);
  const [busy, setBusy] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    axios.get(`${API}/wallet`, authCfg()).then(({ data }) => setWalletBalance(data.balance || 0)).catch(() => {});
  }, []);

  const subItems = cart.filter((i) => i.type === 'subscription');
  const serviceItems = cart.filter((i) => i.type !== 'subscription');
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const couponDiscount = computeCouponDiscount(subtotal);
  const afterCoupon = Math.max(0, subtotal - couponDiscount);
  const maxRedeem = Math.min(coins, Math.floor(subtotal * 0.2));
  const loyaltyApplied = Math.min(coinsRedeem, maxRedeem, afterCoupon);
  const total = Math.max(0, afterCoupon - loyaltyApplied);
  const hasServices = serviceItems.length > 0;
  const walletUsed = payment === 'wallet' && hasServices ? Math.min(walletBalance, total) : 0;
  const dueAtService = Math.max(0, total - walletUsed);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    let subscribedPlan = null;
    let bookingId = null;
    try {
      // Redeem reward points first (if any)
      if (loyaltyApplied > 0) {
        try {
          const { data } = await axios.post(`${API}/rewards/redeem`, { points: loyaltyApplied }, authCfg());
          setCoins(data.points || 0);
        } catch (e) {
          if (e?.response?.status === 401) { nav('/login'); return; }
          throw e;
        }
      }

      // -------- Pay online (Stripe) branch --------
      if (payment === 'card') {
        // Case 1: subscription-only cart -> Stripe subscription checkout
        if (subItems.length > 0 && !hasServices) {
          const { data } = await axios.post(`${API}/payments/checkout`, {
            purpose: 'subscription',
            plan_id: subItems[0].plan_id,
            origin_url: window.location.origin,
          }, authCfg());
          if (data.checkout_url) { window.location.href = data.checkout_url; return; }
          throw new Error('Could not start Stripe checkout');
        }
        // Case 2: services present -> create booking first, then Stripe booking checkout
        const b = await placeBooking({ date, slot, payment: 'card', address: addr, wallet_paid: 0 });
        if (!b || !b.id) throw new Error('Could not create booking');
        bookingId = b.id;
        // If there was ALSO a subscription in the cart, subscribe it separately (still instant, no charge yet).
        // (For MVP we only Stripe-charge the booking amount; sub items still activate via legacy endpoint.)
        for (const s of subItems) {
          try { await axios.post(`${API}/subscriptions/subscribe`, { plan_id: s.plan_id }, authCfg()); } catch {}
        }
        const { data } = await axios.post(`${API}/payments/checkout`, {
          purpose: 'booking', booking_id: bookingId, origin_url: window.location.origin,
        }, authCfg());
        if (data.checkout_url) { window.location.href = data.checkout_url; return; }
        throw new Error('Could not start Stripe checkout');
      }

      // -------- Legacy branch (cash / upi / wallet) --------
      for (const s of subItems) {
        try {
          const { data } = await axios.post(`${API}/subscriptions/subscribe`, { plan_id: s.plan_id }, authCfg());
          subscribedPlan = data;
        } catch (e) {
          if (e?.response?.status === 401) { nav('/login'); return; }
          throw e;
        }
      }
      if (hasServices) {
        const b = await placeBooking({ date, slot, payment, address: addr, wallet_paid: walletUsed });
        if (b && b.id) bookingId = b.id;
      } else {
        clearCart();
      }
      setCoinsRedeem(0);
      if (coupon) clearCoupon();
      if (bookingId) nav(`/booking/${bookingId}`);
      else if (subscribedPlan) nav('/subscriptions');
      else nav('/bookings');
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || 'Could not complete checkout. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const today = new Date();
  const dates = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    return d;
  });

  return (
    <div className="min-h-screen bg-neutral-50 pb-28 md:pb-8">
      <DesktopHeader />

      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-100">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-lg font-extrabold">Book Slot</h1>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <h1 className="hidden md:block text-2xl font-extrabold mb-5">Book your slot</h1>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            {/* Subscription summary */}
            {subItems.length > 0 && (
              <section className="rounded-xl p-4 text-white" style={{ background: 'linear-gradient(135deg,#2E7D32 0%,#43A047 100%)' }}>
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  <p className="font-bold text-sm">Subscription in this order</p>
                </div>
                <div className="mt-2 space-y-1">
                  {subItems.map((s) => (
                    <p key={s.id} className="text-sm">{s.plan_name} · ₹{s.price}/{s.period}</p>
                  ))}
                </div>
                <p className="text-[11px] opacity-90 mt-2">Auto-renews on the same date every {subItems[0]?.period}. Cancel any time.</p>
              </section>
            )}

            {/* Address */}
            <section className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-[#2E7D32]" />
                <p className="font-bold text-sm">Service address</p>
              </div>
              <input value={addr} onChange={(e) => setAddr(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20" />
            </section>

            {hasServices && (
            <>
            <section className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#2E7D32]" />
                <p className="font-bold text-sm">Select date</p>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {dates.map((d) => {
                  const iso = d.toISOString().split('T')[0];
                  const active = iso === date;
                  return (
                    <button key={iso} onClick={() => setDate(iso)}
                      className={`min-w-[60px] py-2 rounded-lg border transition-colors ${active ? 'bg-[#2E7D32] border-[#2E7D32] text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-[#2E7D32]'}`}>
                      <div className="text-[11px]">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                      <div className="font-bold text-lg leading-none">{d.getDate()}</div>
                      <div className="text-[10px] opacity-80">{d.toLocaleDateString('en-US', { month: 'short' })}</div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[#2E7D32]" />
                <p className="font-bold text-sm">Select time slot</p>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {SLOTS.map((s) => {
                  const active = s === slot;
                  return (
                    <button key={s} onClick={() => setSlot(s)}
                      className={`py-2 rounded-lg border text-sm font-medium transition-colors ${active ? 'bg-[#2E7D32] border-[#2E7D32] text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-[#2E7D32]'}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </section>
            </>
            )}

            <section className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="font-bold text-sm mb-3">Payment method</p>
              <div className="space-y-2">
                <PayOption
                  icon={CreditCard}
                  label="Pay online (Stripe)"
                  value="card"
                  current={payment}
                  onChange={setPayment}
                  badge="Recommended"
                />
                <PayOption icon={WalletIcon} label="Cash on service" value="cash" current={payment} onChange={setPayment} />
                <PayOption icon={CreditCard} label="UPI (Pay after service)" value="upi" current={payment} onChange={setPayment} />
                <PayOption
                  icon={WalletIcon}
                  label={`Pay from wallet · ₹${walletBalance}`}
                  value="wallet"
                  current={payment}
                  onChange={setPayment}
                  disabled={walletBalance === 0 || !hasServices}
                  disabledText={!hasServices ? 'Wallet is not applicable for subscription-only orders' : 'Add money to your wallet to use'}
                />
              </div>
              {payment === 'card' && (
                <div className="mt-2 text-xs text-[#1B5E20] bg-[#E8F5E9] rounded-lg px-3 py-2" data-testid="checkout-stripe-note">
                  You'll be redirected to Stripe (test mode). Use card <span className="font-mono font-bold">4242 4242 4242 4242</span>.
                </div>
              )}
              {payment === 'wallet' && hasServices && (
                <div className="mt-2 text-xs text-[#2E7D32] bg-[#E8F5E9] rounded-lg px-3 py-2" data-testid="checkout-wallet-note">
                  ₹{walletUsed} will be deducted from wallet. {dueAtService > 0 ? `₹${dueAtService} due at service (cash/UPI).` : 'No amount due at service.'}
                </div>
              )}
            </section>
          </div>

          {/* Summary sticky sidebar */}
          <div className="md:sticky md:top-24 md:self-start">
            <section className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="font-bold text-sm mb-3">Order summary</p>
              <div className="space-y-1.5 text-sm">
                {cart.map((i) => (
                  <div key={i.id} className="flex justify-between text-neutral-700">
                    <span className="truncate pr-2">{i.name} × {i.qty}</span>
                    <span className="whitespace-nowrap">₹{i.price * i.qty}</span>
                  </div>
                ))}
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-[#2E7D32]" data-testid="checkout-summary-coupon"><span>Coupon ({coupon?.code})</span><span>- ₹{couponDiscount}</span></div>
                )}
                {loyaltyApplied > 0 && (
                  <div className="flex justify-between text-[#D97706]" data-testid="checkout-summary-loyalty"><span>Coins ({loyaltyApplied})</span><span>- ₹{loyaltyApplied}</span></div>
                )}
                <div className="border-t border-dashed border-neutral-200 my-2" />
                <div className="flex justify-between font-bold text-base" data-testid="checkout-summary-total"><span>Total</span><span>₹{total}</span></div>
              </div>
              <button data-testid="checkout-confirm-btn" onClick={confirm} disabled={cart.length === 0 || busy} className="mt-4 hidden md:block w-full bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white rounded-lg py-3 font-semibold transition-colors">
                {busy ? 'Processing…' : `Confirm booking • ₹${total}`}
              </button>
              <div className="mt-3 pt-3 border-t border-neutral-100 flex items-start gap-2 text-[11px] text-neutral-500">
                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#2E7D32]" />
                <p>Cash / UPI accepted. No prepayment required.</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-100 px-4 py-3 z-40">
        <button data-testid="checkout-mobile-confirm-btn" onClick={confirm} disabled={cart.length === 0 || busy} className="w-full bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white rounded-xl py-3.5 font-semibold">
          {busy ? 'Processing…' : `Confirm booking • ₹${total}`}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

const PayOption = ({ icon: Icon, label, value, current, onChange, disabled = false, disabledText, badge }) => {
  const active = current === value;
  return (
    <button onClick={() => !disabled && onChange(value)} disabled={disabled}
      data-testid={`checkout-pay-${value}`}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        disabled ? 'border-neutral-100 bg-neutral-50 opacity-60 cursor-not-allowed'
        : active ? 'border-[#2E7D32] bg-[#E8F5E9]' : 'border-neutral-200 hover:border-neutral-300'}`}>
      <Icon className="w-4 h-4 text-neutral-700" />
      <div className="flex-1 text-left">
        <span className="text-sm font-medium">{label}</span>
        {badge && <span className="ml-2 text-[10px] font-bold bg-[#2E7D32] text-white px-1.5 py-0.5 rounded">{badge}</span>}
        {disabled && disabledText && <span className="text-[10px] text-neutral-400 block mt-0.5">{disabledText}</span>}
      </div>
      <span className={`w-4 h-4 rounded-full border-2 ${active ? 'border-[#2E7D32] bg-[#2E7D32]' : 'border-neutral-300'} flex items-center justify-center`}>
        {active && <Check className="w-2.5 h-2.5 text-white" />}
      </span>
    </button>
  );
};

export default Checkout;
